import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Brain, BatteryWarning, Activity, Dna, Zap, Quote, CheckCircle2, ShieldCheck, ArrowRight, Star, Clock, BookOpen, Coffee, Plane, ListChecks } from 'lucide-react';
import { CountdownBar } from '../components/CountdownBar';
import { CTAButton } from '../components/CTAButton';
import { FAQAccordion } from '../components/FAQAccordion';
import { Footer } from '../components/Footer';
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};
export function LandingPage() {
  const faqs = [{
    question: '"¿Cuánto tiempo al día necesito?"',
    answer: 'El protocolo está diseñado para ejecutivos. Solo necesitas ajustar hábitos que ya haces (cómo comes, cómo te duchas, cómo duermes). No te quitará más de 15 minutos extras al día.'
  }, {
    question: '"¿Tengo que comprar suplementos caros?"',
    answer: 'No. El protocolo se basa en biología aplicada (frío, luz, ayuno). Los suplementos son opcionales para acelerar resultados, y te enseñamos a elegir los más económicos y efectivos.'
  }, {
    question: '"Soy escéptico. ¿Hay ciencia detrás?"',
    answer: 'Cada paso del método está respaldado por estudios de longevidad de 2024 y 2025. Al final del libro tienes la biblioteca de referencias científicas.'
  }];
  return <div className="min-h-screen bg-marble text-slate-900 selection:bg-emerald/20" data-id="element-82">
      <CountdownBar data-id="element-83" />

      {/* BRAND HEADER */}
      <header className="w-full border-b border-slate-200 bg-white/90 backdrop-blur-md sticky top-[48px] md:top-[52px] z-40" data-id="element-84">
        <div className="max-w-5xl mx-auto px-4 py-4 flex justify-center md:justify-start" data-id="element-85">
          <img src="https://cdn.magicpatterns.com/uploads/exH9G2rgUuiMQFMLYuFBer/Captura_de_pantalla_2026-06-11_a_la(s)_11.21.26_a.m..png" alt="MinHealthGroup" className="h-8 md:h-10 w-auto object-contain mix-blend-multiply" data-id="element-86" />
        </div>
      </header>

      {/* HERO SECTION */}
      <section className="relative pt-20 pb-32 px-4 overflow-hidden bg-marble" data-id="element-87">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-white via-marble to-marble -z-10" data-id="element-88"></div>
        <div className="max-w-4xl mx-auto text-center" data-id="element-89">
          <motion.div initial="hidden" animate="visible" variants={fadeIn} data-id="element-90">
            <span className="inline-block py-1 px-4 rounded-full bg-emerald/5 text-emerald border border-emerald/10 text-sm font-semibold tracking-wide mb-6 uppercase" data-id="element-91">
              Protocolo de Longevidad Exclusivo
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-emerald leading-tight mb-8" data-id="element-92">
              ¿Es Posible Recuperar la Claridad Mental y la Energía de tus 30s{' '}
              <span className="text-walnut italic font-serif" data-id="element-93">
                sin Renunciar a tu Estilo de Vida?
              </span>
            </h1>
            <p className="text-lg md:text-xl text-slate-600 mb-10 max-w-3xl mx-auto leading-relaxed" data-id="element-94">
              Cómo Revertir tu Edad Biológica en 30 Días con el Protocolo de
              Longevidad de MinHealthGroup — Incluso si tienes una agenda
              agotadora y crees que "ya lo has intentado todo".
            </p>
            <motion.div initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            delay: 0.3,
            ease: 'easeOut'
          }} className="mb-12" data-id="element-95">
              <div className="relative max-w-3xl mx-auto" data-id="element-96">
                <div className="absolute -inset-4 bg-emerald/5 blur-3xl rounded-full -z-10" data-id="element-97"></div>
                <img src="https://cdn.magicpatterns.com/uploads/4ggPcHvSP7joc89cQkQ2np/452B.png" alt="El Protocolo de Longevidad 45+ de MinHealthGroup: ebook principal, guía de suplementos NAD+ y protocolo anti-jet lag" className="w-full rounded-2xl shadow-soft-lg ring-1 ring-slate-200" data-id="element-98" />
              </div>
            </motion.div>
            <CTAButton data-id="element-99" />
          </motion.div>
        </div>
      </section>

      {/* SECTION 1: THE HOOK */}
      <section className="py-24 px-4 bg-emerald text-marble border-y border-emerald-light" data-id="element-100">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeIn} className="max-w-3xl mx-auto text-center" data-id="element-101">
          <Brain className="w-12 h-12 text-gold mx-auto mb-6 opacity-90" data-id="element-102" />
          <p className="text-xl md:text-2xl leading-relaxed text-marble font-medium font-serif" data-id="element-103">
            ¿Alguna vez has sentido que, aunque tu mente sigue llena de
            ambición, tu cuerpo ha empezado a enviarte señales de retirada?
          </p>
          <div className="w-16 h-px bg-gold/50 mx-auto my-8" data-id="element-104"></div>
          <p className="text-lg text-marble/80 leading-relaxed" data-id="element-105">
            Esa "niebla mental" a las 3 de la tarde, el cansancio que no se va
            con el café, o ver en el espejo a alguien que parece mucho más
            agotado de lo que te sientes por dentro...{' '}
            <strong className="text-white font-semibold" data-id="element-106">
              No es el paso del tiempo. Es un sistema biológico desoptimizado.
            </strong>
          </p>
        </motion.div>
      </section>

      {/* SECTION 2: PAIN AGITATION */}
      <section className="py-24 px-4 relative bg-marble" data-id="element-107">
        <div className="max-w-4xl mx-auto" data-id="element-108">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="grid md:grid-cols-2 gap-12 items-center" data-id="element-109">
            <motion.div variants={fadeIn} data-id="element-110">
              <h2 className="text-3xl md:text-4xl font-bold text-emerald mb-6" data-id="element-111">
                La medicina tradicional tiene una frase peligrosa para los
                líderes de más de 45 años:
                <span className="block text-walnut mt-2 italic" data-id="element-112">
                  "Es normal para tu edad".
                </span>
              </h2>
              <div className="space-y-4 text-slate-700 text-lg" data-id="element-113">
                <p className="flex items-start gap-3" data-id="element-114">
                  <BatteryWarning className="w-6 h-6 text-walnut shrink-0 mt-1" data-id="element-115" />
                  <span data-id="element-116">
                    Es "normal" tener los niveles de testosterona por los
                    suelos.
                  </span>
                </p>
                <p className="flex items-start gap-3" data-id="element-117">
                  <BatteryWarning className="w-6 h-6 text-walnut shrink-0 mt-1" data-id="element-118" />
                  <span data-id="element-119">Es "normal" despertarse cansado.</span>
                </p>
                <p className="flex items-start gap-3" data-id="element-120">
                  <BatteryWarning className="w-6 h-6 text-walnut shrink-0 mt-1" data-id="element-121" />
                  <span data-id="element-122">
                    Es "normal" perder la agudeza mental que te llevó a la cima.
                  </span>
                </p>
              </div>
              <p className="mt-8 text-xl font-semibold text-emerald border-l-2 border-gold pl-4" data-id="element-123">
                Nosotros decimos que eso es inaceptable.
              </p>
            </motion.div>
            <motion.div variants={fadeIn} className="bg-white p-8 rounded-2xl border border-slate-200 shadow-soft relative overflow-hidden" data-id="element-124">
              <Activity className="w-10 h-10 text-walnut mb-6" data-id="element-125" />
              <p className="text-slate-700 leading-relaxed" data-id="element-126">
                Cada día que pasas con inflamación crónica (Inflam-aging) no
                solo estás perdiendo energía; estás acelerando el desgaste de
                tus arterias, de tus neuronas y de tu ADN.
              </p>
              <p className="mt-4 text-slate-700 leading-relaxed" data-id="element-127">
                Si no cambias la señal biológica que tus células están
                recibiendo ahora mismo, el declive no será gradual...{' '}
                <strong className="text-emerald" data-id="element-128">
                  será un muro contra el que chocarás tarde o temprano.
                </strong>
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 3: THE REVELATION */}
      <section className="py-24 px-4 bg-walnut text-marble border-y border-walnut-light" data-id="element-129">
        <div className="max-w-4xl mx-auto text-center" data-id="element-130">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} data-id="element-131">
            <Dna className="w-16 h-16 text-gold mx-auto mb-8" data-id="element-132" />
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8" data-id="element-133">
              Tras años analizando la brecha entre los laboratorios de
              longevidad de Stanford y la vida real de los ejecutivos,
              descubrimos la verdad:
            </h2>
            <p className="text-2xl text-gold font-serif italic mb-10" data-id="element-134">
              La vitalidad es una instrucción genética.
            </p>
            <p className="text-lg text-marble/90 max-w-3xl mx-auto leading-relaxed mb-8" data-id="element-135">
              No necesitas vivir en un gimnasio ni gastar $50,000 en máquinas de
              Silicon Valley. Solo necesitas activar la{' '}
              <strong className="text-white" data-id="element-136">Autofagia Celular</strong> y
              optimizar tu producción de{' '}
              <strong className="text-white" data-id="element-137">NAD+</strong>.
            </p>
            <div className="inline-block bg-white/5 border border-gold/20 rounded-xl p-6 backdrop-blur-sm" data-id="element-138">
              <p className="text-xl text-white font-medium flex items-center justify-center gap-3" data-id="element-139">
                <Zap className="text-gold w-6 h-6" data-id="element-140" />
                Hemos destilado esta ciencia compleja en un protocolo que puedes
                ejecutar en los "espacios en blanco" de tu agenda. Bienvenido al
                Lujo Silencioso de la salud.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 4: SOCIAL PROOF */}
      <section className="py-24 px-4 bg-marble" data-id="element-141">
        <div className="max-w-5xl mx-auto" data-id="element-142">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="grid md:grid-cols-2 gap-8" data-id="element-143">
            <motion.div variants={fadeIn} className="bg-white border border-slate-200 shadow-soft p-8 rounded-2xl relative" data-id="element-144">
              <Quote className="absolute top-6 right-6 w-12 h-12 text-slate-100" data-id="element-145" />
              <div className="flex gap-1 mb-6" data-id="element-146">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-gold text-gold" data-id="element-147" />)}
              </div>
              <h3 className="text-xl font-bold text-emerald mb-4 font-serif" data-id="element-148">
                "Recuperé el enfoque que tenía a los 25 años"
              </h3>
              <p className="text-slate-600 mb-6 leading-relaxed italic" data-id="element-149">
                "Como CEO, no puedo permitirme una tarde de baja energía. Con el
                protocolo de MinHealth, mi claridad mental volvió en la primera
                semana. He perdido 6kg de inflamación y mi Oura Ring muestra una
                recuperación récord."
              </p>
              <div className="border-t border-slate-100 pt-4" data-id="element-150">
                <p className="font-semibold text-emerald" data-id="element-151">Ricardo M., Madrid</p>
                <p className="text-walnut text-sm mt-1" data-id="element-152">
                  Resultado: Reversión de 7 años biológicos en 3 meses.
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeIn} className="bg-white border border-slate-200 shadow-soft p-8 rounded-2xl relative" data-id="element-153">
              <Quote className="absolute top-6 right-6 w-12 h-12 text-slate-100" data-id="element-154" />
              <div className="flex gap-1 mb-6" data-id="element-155">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-gold text-gold" data-id="element-156" />)}
              </div>
              <h3 className="text-xl font-bold text-emerald mb-4 font-serif" data-id="element-157">
                "Por fin una solución para gente ocupada"
              </h3>
              <p className="text-slate-600 mb-6 leading-relaxed italic" data-id="element-158">
                "Había probado dietas y suplementos sin orden. MinHealth me dio
                la estructura que necesitaba. Mi piel se ve más joven y, por
                primera vez en una década, me despierto antes de que suene la
                alarma."
              </p>
              <div className="border-t border-slate-100 pt-4" data-id="element-159">
                <p className="font-semibold text-emerald" data-id="element-160">
                  Elena G., Ciudad de México
                </p>
                <p className="text-walnut text-sm mt-1" data-id="element-161">
                  Resultado: Mejora del 40% en Variabilidad de Frecuencia
                  Cardíaca.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 5: THE OFFER */}
      <section className="py-24 px-4 bg-emerald text-marble border-t border-emerald-light" data-id="element-162">
        <div className="max-w-4xl mx-auto" data-id="element-163">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="text-center mb-16" data-id="element-164">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-165">
              Presentamos:{' '}
              <span className="text-gold italic" data-id="element-166">
                El Protocolo de Longevidad 45+
              </span>
            </h2>
            <p className="text-xl text-marble/90" data-id="element-167">
              El sistema definitivo de MinHealthGroup para detener el declive y
              optimizar cada célula de tu cuerpo.
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="bg-emerald-dark/50 border border-gold/20 rounded-3xl p-8 md:p-12 shadow-2xl" data-id="element-168">
            <div className="mb-10" data-id="element-169">
              <img src="https://cdn.magicpatterns.com/uploads/4ggPcHvSP7joc89cQkQ2np/452B.png" alt="El Protocolo de Longevidad 45+ de MinHealthGroup: ebook principal, guía de suplementos NAD+ y protocolo anti-jet lag" className="w-full rounded-2xl shadow-soft-lg ring-1 ring-white/10" data-id="element-170" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-8 text-center border-b border-white/10 pb-6 font-serif" data-id="element-171">
              Lo que vas a recibir hoy:
            </h3>

            <div className="space-y-6" data-id="element-172">
              <div className="flex gap-4 items-start" data-id="element-173">
                <BookOpen className="w-8 h-8 text-gold shrink-0 mt-1" data-id="element-174" />
                <div data-id="element-175">
                  <h4 className="text-xl font-bold text-white" data-id="element-176">
                    Ebook Principal (60 páginas)
                  </h4>
                  <p className="text-marble/80 mt-1" data-id="element-177">
                    El Manual Maestro de Biohacking y Reversión Biológica.
                  </p>
                  <p className="text-gold/70 text-sm mt-1" data-id="element-178">Valor: $197</p>
                </div>
              </div>

              <div className="flex gap-4 items-start" data-id="element-179">
                <Activity className="w-8 h-8 text-gold shrink-0 mt-1" data-id="element-180" />
                <div data-id="element-181">
                  <h4 className="text-xl font-bold text-white" data-id="element-182">
                    Bonus 1: El Directorio de Suplementos NAD+
                  </h4>
                  <p className="text-marble/80 mt-1" data-id="element-183">
                    No tires tu dinero en basura comercial. Ve directo a lo que
                    funciona.
                  </p>
                  <p className="text-gold/70 text-sm mt-1" data-id="element-184">Valor: $97</p>
                </div>
              </div>

              <div className="flex gap-4 items-start" data-id="element-185">
                <Coffee className="w-8 h-8 text-gold shrink-0 mt-1" data-id="element-186" />
                <div data-id="element-187">
                  <h4 className="text-xl font-bold text-white" data-id="element-188">
                    Bonus 2: Guía de Comidas en 15 Minutos
                  </h4>
                  <p className="text-marble/80 mt-1" data-id="element-189">
                    Nutrición de élite para gente que no tiene tiempo de
                    cocinar.
                  </p>
                  <p className="text-gold/70 text-sm mt-1" data-id="element-190">Valor: $67</p>
                </div>
              </div>

              <div className="flex gap-4 items-start" data-id="element-191">
                <Plane className="w-8 h-8 text-gold shrink-0 mt-1" data-id="element-192" />
                <div data-id="element-193">
                  <h4 className="text-xl font-bold text-white" data-id="element-194">
                    Bonus 3: Protocolo Anti-Jet Lag
                  </h4>
                  <p className="text-marble/80 mt-1" data-id="element-195">
                    Domina las zonas horarias y aterriza listo para negociar.
                  </p>
                  <p className="text-gold/70 text-sm mt-1" data-id="element-196">Valor: $47</p>
                </div>
              </div>

              <div className="flex gap-4 items-start" data-id="element-197">
                <ListChecks className="w-8 h-8 text-gold shrink-0 mt-1" data-id="element-198" />
                <div data-id="element-199">
                  <h4 className="text-xl font-bold text-white" data-id="element-200">
                    Bonus 4 al 15
                  </h4>
                  <p className="text-marble/80 mt-1" data-id="element-201">
                    Checklist de Sueño, Bio-Marcadores, Rutinas Express y más.
                  </p>
                  <p className="text-gold/70 text-sm mt-1" data-id="element-202">Valor: $144</p>
                </div>
              </div>
            </div>

            <div className="mt-10 pt-8 border-t border-white/10 text-center" data-id="element-203">
              <p className="text-2xl text-marble/80 font-medium font-serif" data-id="element-204">
                VALOR TOTAL ACUMULADO:{' '}
                <span className="line-through decoration-walnut-light" data-id="element-205">
                  $552
                </span>
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 6: PRICE & URGENCY */}
      <section id="pricing" className="py-24 px-4 relative overflow-hidden bg-marble" data-id="element-206">
        <div className="max-w-3xl mx-auto text-center" data-id="element-207">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} data-id="element-208">
            <p className="text-xl text-slate-700 mb-6" data-id="element-209">
              Hoy no pagarás los $552 que cuesta este sistema por separado. Ni
              siquiera pagaras los $197 que será su precio oficial.
            </p>
            <p className="text-2xl text-emerald font-semibold mb-10 font-serif" data-id="element-210">
              Como parte de nuestro lanzamiento mundial...
            </p>

            <div className="bg-white border border-slate-200 shadow-soft-lg rounded-3xl p-10 mb-10" data-id="element-211">
              <p className="text-lg text-walnut font-medium mb-4 uppercase tracking-wider" data-id="element-212">
                Consigue el Sistema Completo HOY por solo:
              </p>
              <div className="flex justify-center items-start gap-2 mb-6" data-id="element-213">
                <span className="text-4xl font-bold text-emerald mt-2" data-id="element-214">$</span>
                <span className="text-8xl font-bold text-emerald tracking-tighter" data-id="element-215">
                  47
                </span>
              </div>
              <div className="w-24 h-px bg-gold mx-auto mb-8" data-id="element-216"></div>
              <p className="text-slate-500 mb-8" data-id="element-217">
                Esta es una inversión única. Sin suscripciones. Sin cargos
                ocultos.
              </p>
              <CTAButton data-id="element-218" />
              <p className="text-sm text-walnut mt-6 flex items-center justify-center gap-2" data-id="element-219">
                <Clock className="w-4 h-4" data-id="element-220" />
                El precio subirá a $197 en cuanto el contador llegue a cero.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 7: FAQ */}
      <section className="py-24 px-4 bg-slate-50 border-y border-slate-200" data-id="element-221">
        <div className="max-w-4xl mx-auto" data-id="element-222">
          <div className="text-center mb-16" data-id="element-223">
            <h2 className="text-3xl md:text-4xl font-bold text-emerald mb-4 font-serif" data-id="element-224">
              Preguntas Frecuentes
            </h2>
            <p className="text-slate-600" data-id="element-225">
              Todo lo que necesitas saber antes de empezar.
            </p>
          </div>
          <FAQAccordion faqs={faqs} data-id="element-226" />
        </div>
      </section>

      {/* SECTION 8: GUARANTEE */}
      <section className="py-24 px-4 bg-marble" data-id="element-227">
        <div className="max-w-3xl mx-auto" data-id="element-228">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="bg-white border border-gold/30 shadow-soft-lg rounded-3xl p-8 md:p-12 text-center relative overflow-hidden" data-id="element-229">
            <ShieldCheck className="w-20 h-20 text-gold mx-auto mb-6" data-id="element-230" />
            <h2 className="text-3xl font-bold text-emerald mb-6 font-serif" data-id="element-231">
              Garantía De Vitalidad de 30 Días
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-6" data-id="element-232">
              Si en 30 días no sientes que tu claridad mental ha vuelto, si no
              te ves mejor en el espejo o si simplemente sientes que esto no es
              para ti, envíanos un email. Te devolveremos cada centavo, sin
              preguntas.
            </p>
            <p className="text-xl font-semibold text-walnut" data-id="element-233">
              Nosotros nos quedamos con el riesgo, tú te quedas con los
              resultados.
            </p>
          </motion.div>
        </div>
      </section>

      {/* SECTION 9: THE CLOSE */}
      <section className="py-24 px-4 bg-walnut text-marble border-t border-walnut-light" data-id="element-234">
        <div className="max-w-4xl mx-auto" data-id="element-235">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="text-center mb-16" data-id="element-236">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 font-serif" data-id="element-237">
              Tienes dos caminos frente a ti:
            </h2>

            <div className="grid md:grid-cols-2 gap-8 mb-16 text-left" data-id="element-238">
              <div className="bg-white/5 border border-white/10 p-8 rounded-2xl" data-id="element-239">
                <h3 className="text-xl font-bold text-marble/80 mb-4 flex items-center gap-2" data-id="element-240">
                  <span className="bg-white/10 w-8 h-8 rounded-full flex items-center justify-center text-sm" data-id="element-241">
                    A
                  </span>
                  No hacer nada
                </h3>
                <p className="text-marble/60 leading-relaxed" data-id="element-242">
                  Seguir aceptando el declive gradual, la niebla mental y el
                  cansancio como "normales", despertándote dentro de un año
                  sintiéndote aún más viejo.
                </p>
              </div>

              <div className="bg-emerald border border-gold/30 p-8 rounded-2xl relative overflow-hidden shadow-soft-lg" data-id="element-243">
                <h3 className="text-xl font-bold text-gold mb-4 flex items-center gap-2" data-id="element-244">
                  <span className="bg-gold/20 w-8 h-8 rounded-full flex items-center justify-center text-sm text-gold" data-id="element-245">
                    B
                  </span>
                  Unirte a la élite
                </h3>
                <p className="text-marble/90 leading-relaxed" data-id="element-246">
                  Unirte a{' '}
                  <strong className="text-white" data-id="element-247">MinHealthGroup</strong>.
                  Invertir hoy menos de lo que cuesta una cena y empezar a
                  revertir tu edad biológica desde mañana mismo.
                </p>
              </div>
            </div>

            <p className="text-2xl text-gold font-serif italic mb-10" data-id="element-248">
              La decisión es tuya.
            </p>

            <CTAButton className="w-full md:w-auto text-sm md:text-lg px-4 md:px-10" data-id="element-249" />
          </motion.div>
        </div>
      </section>

      <Footer data-id="element-250" />
    </div>;
}